import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { z } from "zod";
import { NavBar, NavBarTitleProvider } from "components/NavBar";
import { AppDataProvider } from "store/appData";
import { DatasetViewerSettingsProvider } from "views/DatasetViewer/hooks/useDatasetViewerSettings";
import axiosInstance from "utils/request";

type AuthState = "loading" | "authenticated" | "unauthenticated";

const AuthResponseSchema = z.object({ loggedIn: z.boolean() });

export default function ProtectedRoute() {
  const [authState, setAuthState] = useState<AuthState>("loading");
  const location = useLocation();

  useEffect(() => {
    let mounted = true;
    async function checkAuth() {
      try {
        const { data } = await axiosInstance.get("/auth");
        const { loggedIn } = AuthResponseSchema.parse(data);
        if (mounted) setAuthState(loggedIn ? "authenticated" : "unauthenticated");
      } catch {
        if (mounted) setAuthState("unauthenticated");
      }
    }
    checkAuth();
    return () => {
      mounted = false;
    };
  }, []);

  if (authState === "loading") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (authState === "unauthenticated") {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }

  return (
    <AppDataProvider>
      <NavBarTitleProvider>
        <DatasetViewerSettingsProvider>
          <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
            <NavBar />
            <Box sx={{ flex: 1, overflow: "auto" }}>
              <Outlet />
            </Box>
          </Box>
        </DatasetViewerSettingsProvider>
      </NavBarTitleProvider>
    </AppDataProvider>
  );
}
